/* eslint-disable ember/no-get, ember/no-new-mixins, no-restricted-properties */
import Mixin from '@ember/object/mixin';
import { A } from '@ember/array';
import { computed, get, set } from '@ember/object';
import { mapBy, sum } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import { filterValue } from '@linc-technologies/linced-common/utils/filtering';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';

export default Mixin.create({
  strings: service(),

  from: '',
  to: '',
  allNonCancelledTransactions: computed(
    'model.transactions.@each.isCancelled',
    function () {
      return this.model.transactions.filter((item) => !item.isCancelled);
    },
  ),
  transactionDisplayAmounts: mapBy(
    'allNonCancelledTransactions',
    'displayAmount',
  ),
  ledgerTotal: sum('transactionDisplayAmounts'),
  taxAmounts: mapBy('allNonCancelledTransactions', 'taxAmount'),
  taxTotal: sum('taxAmounts'),

  filteredTransactionDisplayAmounts: mapBy(
    'nonCancelledFilteredTransactions',
    'displayAmount',
  ),
  filteredLedgerTotal: sum('filteredTransactionDisplayAmounts'),
  filteredTaxAmounts: mapBy('nonCancelledFilteredTransactions', 'taxAmount'),
  filteredTaxTotal: sum('filteredTaxAmounts'),

  filters: A(),

  // filteredTransactions applies the user's filters to the list of transactions.
  filteredTransactions: computed(
    'model.transactions.[]',
    'filters.@each.{field,value}',
    function () {
      if (isEmpty(this.filters)) {
        // No filters - just return the data.
        return this.model.transactions;
      }

      // Map raw filters into what we need to actually perform the filtering.
      let filters = this.filters.map((filter) => {
        let dataType = DataType.STRING;
        let key = filter.field;
        switch (filter.field) {
          case 'firstName':
          case 'lastName':
            key = `account.person.${filter.field}`;
            break;
          case 'displayAmount':
          case 'taxAmount':
            dataType = DataType.FLOAT;
            break;
        }

        return {
          key,
          dataType,
          value: filter.value,
        };
      });

      // Apply all filters to each row in one hit, to avoid iterating over a (potentially) large dataset multiple times.
      return this.model.transactions.filter((row) => {
        return filters.every((filter) => {
          return filterValue(
            get(row, filter.key) ?? '',
            filter.dataType,
            filter.value,
          );
        });
      });
    },
  ),

  nonCancelledFilteredTransactions: computed(
    'filteredTransactions.@each.isCancelled',
    function () {
      return this.filteredTransactions.filter((item) => !item.isCancelled);
    },
  ),
  exportData: computed(
    'ledger.{code,name}',
    'nonCancelledFilteredTransactions.[]',
    function () {
      return this.nonCancelledFilteredTransactions.map((transaction) => {
        return [
          transaction.date,
          `${this.get('ledger.code')} - ${this.get('ledger.name')}`,
          transaction.get('lineItem.name'),
          transaction.get('lineItem.account.person.lastName'),
          transaction.get('lineItem.account.person.firstName'),
          transaction.displayMethod,
          transaction.paidBy,
          '$' + Number(transaction.displayAmount).toFixed(2),
          '$' + Number(transaction.taxAmount).toFixed(2),
        ];
      });
    },
  ),

  filter(field, value) {
    let filter = findBy(this.filters, 'field', field);
    if (isEmpty(filter)) {
      this.filters.pushObject({ field, value });
      return;
    }

    if (isEmpty(value)) {
      this.filters.removeObject(filter);
    } else {
      set(filter, 'value', value);
    }
  },

  // reset runs from the routes' resetController. Both batch routes are
  // parameterised (ledger_id / ledger_item_id), so the title overrides have to
  // be dropped alongside the filters or a title typed for one ledger would
  // follow the user to the next.
  reset() {
    this.set('filters', A());
    this._summaryTitle = null;
    this._reportTitle = null;
  },
});
